/**
 * SupportTriagePanel — the workflow controls for ONE selected support ticket: the ASSIGNMENT control
 * (assign-to-me / unassign), the STATUS control (a Select restricted to the legal lifecycle edges), the
 * (read-only) PRIORITY badge, and the RESOLVE action. It reads the ticket's current version off the passed
 * detail and rides it as the optimistic-concurrency guard on every write, so a stale write is a typed 409
 * the panel surfaces as "reload then retry" (never a lost update).
 *
 * It does NOT re-fetch the ticket — the parent (SupportChatPanel) owns the live `useSupportTicket` read and
 * passes the detail down, so the panel + the chat thread share one poll and re-derive together whenever a
 * write invalidates the management root.
 *
 * Accessibility: the assignment + status controls carry real labels; the priority badge pairs tone with the
 * label glyph (never color-only); the assignment line names the current assignee. AA contrast via tokens.
 */
import { useEffect, useState } from "react";
import { Badge, Button, FormField, Select, useToast } from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import { useAuthStore } from "@/auth";
import { prioriteGlyph, prioriteLabelKey, prioriteTone, statutLabelKey } from "../support.lib";
import { allowedNextStatuts } from "../supportWorkflow.lib";
import { useAssignTicket, useChangeStatut } from "../supportWorkflow.api";
import type { SupportTicketDetail } from "../support.types";
import type { TicketStatut } from "../types";
import { SupportResolveModal } from "./SupportResolveModal";

export interface SupportTriagePanelProps {
  /** The open ticket detail (carries the current statut/priorite/assignedTo/version). */
  ticket: SupportTicketDetail;
  /** Gate (super-admin && online) — disables the controls when off. */
  enabled: boolean;
}

/** Surface a write error as the right localized toast (409 CONFLICT → reload-then-retry). */
function useWriteError() {
  const { t } = useT();
  const toast = useToast();
  return (err: unknown) => {
    if (err instanceof ApiError && (err.httpStatus === 409 || err.code === "CONFLICT")) {
      toast.error(t("mgmt.support.wf.triage.conflict"));
    } else {
      toast.error(t("mgmt.support.wf.triage.error"));
    }
  };
}

export function SupportTriagePanel({ ticket, enabled }: SupportTriagePanelProps) {
  const { t } = useT();
  const toast = useToast();
  const onWriteError = useWriteError();
  const selfId = useAuthStore((s) => s.user?.id ?? null);

  const assign = useAssignTicket();
  const changeStatut = useChangeStatut();
  const [resolveOpen, setResolveOpen] = useState(false);

  // The status Select is a controlled local mirror of the ticket statut; it re-syncs when the ticket
  // refetches (a poll/other write changed it) so the operator never acts on a stale selection.
  const [statutDraft, setStatutDraft] = useState<TicketStatut>(ticket.statut);
  useEffect(() => setStatutDraft(ticket.statut), [ticket.statut, ticket.version]);

  const busy = assign.isPending || changeStatut.isPending;
  const disabled = !enabled || busy;

  const isMine = selfId != null && ticket.assignedTo === selfId;

  function onAssignMe(): void {
    if (selfId == null) return;
    assign.mutate(
      { ticketId: ticket.id, assigned_to: selfId, expected_version: ticket.version },
      {
        onSuccess: (res) =>
          toast.success(
            res.previousAssignedTo
              ? t("mgmt.support.wf.triage.reassigned", { prev: res.previousAssignedTo })
              : t("mgmt.support.wf.triage.assigned"),
          ),
        onError: onWriteError,
      },
    );
  }

  function onUnassign(): void {
    assign.mutate(
      { ticketId: ticket.id, assigned_to: null, expected_version: ticket.version },
      {
        onSuccess: () => toast.success(t("mgmt.support.wf.triage.unassigned")),
        onError: onWriteError,
      },
    );
  }

  function onChangeStatut(next: TicketStatut): void {
    setStatutDraft(next);
    if (next === ticket.statut) return; // no-op — nothing to write
    changeStatut.mutate(
      { ticketId: ticket.id, statut: next, expected_version: ticket.version },
      {
        onSuccess: () => toast.success(t("mgmt.support.wf.triage.statut.changed", { statut: t(statutLabelKey(next)) })),
        onError: (err) => {
          setStatutDraft(ticket.statut); // roll the Select back — the server rejected the edge
          onWriteError(err);
        },
      },
    );
  }

  const statutOptions = allowedNextStatuts(ticket.statut).map((s) => ({
    value: s,
    label: t(statutLabelKey(s)),
  }));

  return (
    <section
      aria-label={t("mgmt.support.wf.triage.title")}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--nv-s-3)",
        padding: "var(--nv-s-3)",
        borderRadius: "var(--nv-r-md, 10px)",
        background: "var(--nv-surface-1, var(--nv-surface))",
        border: "1px solid var(--nv-line-faint, var(--nv-border-subtle))",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--nv-s-2)", flexWrap: "wrap" }}>
        <strong style={{ fontSize: "var(--nv-fs-label)" }}>{t("mgmt.support.wf.triage.title")}</strong>
        <span
          className="nv-ltr-island"
          style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))", fontFamily: "var(--nv-font-mono)" }}
        >
          {t("mgmt.support.wf.triage.version", { v: ticket.version })}
        </span>
      </div>

      {/* Priority (read-only — set by the lab at creation) */}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)", flexWrap: "wrap" }}>
        <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-2, var(--nv-slate))" }}>
          {t("mgmt.support.wf.triage.priorite")}
        </span>
        <Badge tone={prioriteTone(ticket.priorite)} glyph={prioriteGlyph(ticket.priorite)}>
          {t(prioriteLabelKey(ticket.priorite))}
        </Badge>
      </div>

      {/* Assignment control */}
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
        <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-2, var(--nv-slate))" }}>
          {t("mgmt.support.wf.triage.assignee")}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)", flexWrap: "wrap" }}>
          <Badge tone={ticket.assignedTo ? "info" : "neutral"} glyph={ticket.assignedTo ? "◉" : "○"}>
            {ticket.assignedTo
              ? t("mgmt.support.wf.triage.assignee.current", { id: ticket.assignedTo })
              : t("mgmt.support.wf.triage.assignee.none")}
          </Badge>
          {!isMine && (
            <Button size="sm" variant="secondary" onClick={onAssignMe} disabled={disabled || selfId == null}>
              {t("mgmt.support.wf.triage.assignee.me")}
            </Button>
          )}
          {ticket.assignedTo && (
            <Button size="sm" variant="ghost" onClick={onUnassign} disabled={disabled}>
              {t("mgmt.support.wf.triage.assignee.unassign")}
            </Button>
          )}
        </div>
      </div>

      {/* Status control (restricted to legal edges) + resolve action */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--nv-s-3)", flexWrap: "wrap" }}>
        <FormField label={t("mgmt.support.wf.triage.statut")}>
          {(controlProps) => (
            <Select
              {...controlProps}
              value={statutDraft}
              onChange={(e) => onChangeStatut(e.target.value as TicketStatut)}
              options={statutOptions}
              disabled={disabled}
            />
          )}
        </FormField>
        <Button
          variant="primary"
          onClick={() => setResolveOpen(true)}
          disabled={disabled || ticket.statut === "ferme"}
        >
          {t("mgmt.support.wf.resolve")}
        </Button>
      </div>

      <SupportResolveModal
        open={resolveOpen}
        ticket={ticket}
        onClose={() => setResolveOpen(false)}
      />
    </section>
  );
}
